import React from 'react';
import { Link } from 'react-router-dom';
import { brand, nav, site } from '../../data/site';
import { categories } from '../../data/collections';
import { IconFacebook, IconInstagram, Lotus } from '../ui/Icons';

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-royal-900 text-cream">
      <div className="pointer-events-none absolute inset-0 paisley-bg opacity-[0.12]" />

      <div className="relative">
        {/* Oversized wordmark */}
        <div className="shell border-b border-cream/10 pb-12 pt-20 lg:pb-16 lg:pt-28">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Lotus className="h-7 w-auto text-gold/60" />
              <p className="mt-6 font-display text-[2.4rem] leading-[0.95] tracking-[0.08em] text-cream sm:text-[3.4rem] lg:text-[4.2rem]">
                PRAMUKH
              </p>
              <p className="mt-3 text-[12px] uppercase tracking-luxe text-gold-300">
                Jewellers · {site.positioning} · Est. {site.since}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <a
                href={site.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-11 w-11 items-center justify-center border border-cream/15 text-cream/80 transition-all duration-500 hover:border-gold hover:text-gold"
              >
                <IconInstagram className="h-4 w-4" />
              </a>
              <a
                href={site.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-11 w-11 items-center justify-center border border-cream/15 text-cream/80 transition-all duration-500 hover:border-gold hover:text-gold"
              >
                <IconFacebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Columns */}
        <div className="shell grid gap-12 py-14 md:grid-cols-2 lg:grid-cols-4 lg:gap-10 lg:py-20">
          <div>
            <h4 className="text-[11.5px] uppercase tracking-luxe text-gold-300">Explore</h4>
            <ul className="mt-6 space-y-3">
              {nav.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className="text-[13.5px] font-light text-cream/80 transition-colors duration-400 hover:text-gold-200"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[11.5px] uppercase tracking-luxe text-gold-300">Collections</h4>
            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-1">
              {categories.slice(0, 8).map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/collections/${c.slug}`}
                    className="text-[13.5px] font-light text-cream/80 transition-colors duration-400 hover:text-gold-200"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[11.5px] uppercase tracking-luxe text-gold-300">Showroom</h4>
            <a
              href={site.social.maps}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 block text-[13.5px] font-light leading-[1.9] text-cream/80 transition-colors duration-400 hover:text-gold-200"
            >
              {site.address.line1},<br />
              {site.address.line2},<br />
              {site.address.city}, {site.address.state} {site.address.pin}
            </a>

            <div className="mt-6 border-t border-cream/10 pt-5">
              {site.hours.map((h) => (
                <p key={h.days} className="flex justify-between gap-4 py-1 text-[12.5px] font-light text-cream/85">
                  <span>{h.days}</span>
                  <span className="text-cream/70">{h.time}</span>
                </p>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-[11.5px] uppercase tracking-luxe text-gold-300">Contact</h4>
            <a
              href={`tel:${site.phone}`}
              className="mt-6 block font-display text-2xl text-cream transition-colors duration-400 hover:text-gold-200"
            >
              {site.phoneDisplay}
            </a>
            <a
              href={`mailto:${site.email}`}
              className="mt-2 block break-all text-[13px] font-light text-cream/75 transition-colors duration-400 hover:text-gold-200"
            >
              {site.email}
            </a>

            <div className="mt-7 flex items-center gap-4">
              <img src={brand.qrInstagram} alt="Instagram QR code" className="h-[72px] w-[72px] bg-cream p-1" />
              <img src={brand.qrLocation} alt="Showroom location QR code" className="h-[72px] w-[72px] bg-cream p-1" />
            </div>
          </div>
        </div>

        {/* Baseline */}
        <div className="border-t border-cream/10">
          <div className="shell flex flex-col items-center justify-between gap-3 py-6 text-[11.5px] uppercase tracking-wider2 text-cream/60 sm:flex-row">
            <p>© {year} {site.legal}</p>
            <p className="hidden lg:block">Ahmedabad · Gujarat · India</p>
            <p>
              Design by{' '}
              <a
                href="https://www.shreepatisystems.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/85 underline-offset-4 transition-colors duration-400 hover:text-gold-200 hover:underline"
              >
                Shreepati Systems
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
